<script>
import * as Sentry from '@sentry/browser';
import { GlAlert, GlIcon, GlTooltipDirective } from '@gitlab/ui';
import { throttle } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';
import ErrorAlert from '~/javascripts/components/error_alert.vue';
import { purchaseAddonSubscription } from '~/javascripts/api';
import { purchaseDotComAddonPayload } from '~/javascripts/subscription_payloads';
import { toLocaleDateString } from '~/javascripts/lib/datetime';
import { sprintf } from '~/javascripts/lib/locale';
import PurchaseSuccessModal from '~/javascripts/purchase/components/purchase_success_modal.vue';
import PurchaseDetailsCard from '~/javascripts/purchase/components/purchase_details_card.vue';
import PurchaseSummaryCard from '~/javascripts/purchase/components/purchase_summary_card.vue';
import PurchaseConfirmationCard from '~/javascripts/purchase/components/purchase_confirmation_card_deprecated.vue';
import PaymentMethodsSelect from '~/javascripts/purchase/shared/components/payment_methods_select.vue';
import ZuoraAvailabilityWrapper from '~/javascripts/components/zuora_availability_wrapper.vue';
import {
  ERROR_CODE_REQUEST_CANCELED,
  DEFAULT_DEBOUNCE_AND_THROTTLE_MS,
} from '~/javascripts/constants';
import { I18N_SUMMARY_DATES, I18N_STORAGE_TOOLTIP_NOTE } from '~/javascripts/purchase/constants';
import {
  ERROR_PROCESSING_PAYMENT_TYPE,
  PURCHASE_ERROR_DICTIONARY,
} from '~/javascripts/purchase/error_dictionary';
import { getCompanyDetails } from '~/javascripts/billing_account/utils';

export default {
  i18n: {
    dates: I18N_SUMMARY_DATES,
    tooltipNote: I18N_STORAGE_TOOLTIP_NOTE,
  },
  name: 'AddonTransactionApp',
  components: {
    GlAlert,
    GlIcon,
    ErrorAlert,
    PurchaseSuccessModal,
    PurchaseDetailsCard,
    PurchaseSummaryCard,
    PurchaseConfirmationCard,
    PaymentMethodsSelect,
    ZuoraAvailabilityWrapper,
  },
  directives: {
    GlTooltip: GlTooltipDirective,
  },
  inject: ['customerBillingAccountsPresent', 'user'],
  props: {
    purchaseDetails: {
      type: String,
      required: false,
      default: '',
    },
    pageTitle: {
      type: String,
      required: true,
    },
    productTitle: {
      type: String,
      required: true,
    },
    productName: {
      type: String,
      required: true,
    },
    chargeDescription: {
      type: String,
      required: false,
      default: '',
    },
    purchaseConfirmationText: {
      type: String,
      required: true,
    },
    informationAlertText: {
      type: String,
      required: false,
      default: '',
    },
    backendErrorMessage: {
      type: String,
      required: false,
      default: '',
    },
    productPrice: {
      type: Number,
      required: true,
    },
    currentNamespaceDetails: {
      type: Object,
      required: true,
    },
    planId: {
      type: String,
      required: true,
    },
    successRedirectPath: {
      type: String,
      required: true,
    },
    showProrationInfo: {
      type: Boolean,
      required: false,
      default: false,
    },
    showExpirationDate: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      quantity: 1,
      selectedPaymentMethodId: null,
      subtotal: 0.0,
      tax: 0.0,
      total: 0.0,
      subscriptionStartDate: null,
      subscriptionEndDate: null,
      abortController: null,
      isLoading: false,
      showSuccessModal: false,
      previewError: null,
      apiError: null,
      idempotencyKeys: {},
      addressForm: {
        address1: this.user.address1,
        address2: this.user.address2,
        country: this.user.country,
        state: this.user.state,
        city: this.user.city,
        zipCode: this.user.zipCode,
      },
      companyDetailsForm: {
        companyName: this.user.companyName,
        taxId: this.user.vatCode,
      },
      addressFormValid: Boolean(this.customerBillingAccountsPresent),
      companyDetailsFormValid: Boolean(this.customerBillingAccountsPresent),
    };
  },
  computed: {
    error() {
      // Setting alert precedence. Only one alert should be displayed at a time.
      return this.backendErrorMessage || this.apiError || this.previewError;
    },
    canProceedWithPayment() {
      return Boolean(this.total >= 0.0 && this.selectedPaymentMethodId && !this.previewError);
    },
    subscriptionPeriod() {
      return sprintf(this.$options.i18n.dates, {
        startDate: toLocaleDateString(this.subscriptionStartDate),
        endDate: toLocaleDateString(this.subscriptionEndDate),
      });
    },
    purchaseButtonDisabled() {
      return (
        !this.canProceedWithPayment ||
        this.isLoading ||
        this.showSuccessModal ||
        !this.addressFormValid ||
        !this.companyDetailsFormValid
      );
    },
    successDescription() {
      return `You've just purchased ${this.quantity} x ${this.productName}`;
    },
    showAdditionalPriceInfo() {
      return this.showProrationInfo && this.subscriptionEndDate;
    },
    showExpirationDateInfo() {
      return this.showExpirationDate && this.subscriptionEndDate;
    },
    displayPurchaseDetails() {
      const details = [];
      if (this.purchaseDetails) {
        details.push(this.purchaseDetails);
      }
      if (this.showExpirationDateInfo) {
        details.push(
          `${toLocaleDateString(this.subscriptionStartDate)} - ${toLocaleDateString(
            this.subscriptionEndDate,
          )}`,
        );
      }
      return details.join(' ');
    },
    idempotencyKeyParams() {
      return {
        namespaceId: this.currentNamespaceDetails.id,
        planId: this.planId,
        quantity: this.quantity,
        selectedPaymentMethodId: this.selectedPaymentMethodId,
      };
    },
    serializedIdempotencyKeyParams() {
      return JSON.stringify(this.idempotencyKeyParams);
    },
  },
  watch: {
    idempotencyKeyParams: {
      handler() {
        this.updateIdempotencyKey();
      },
      deep: true,
    },
  },
  created() {
    this.previewTransaction();
    this.updateIdempotencyKey();
  },
  methods: {
    getPayload(isPurchase) {
      const sendCustomerInformation = !this.customerBillingAccountsPresent && isPurchase;
      return purchaseDotComAddonPayload({
        planId: this.planId,
        subscriptionName: this.currentNamespaceDetails.subscriptionName,
        namespaceName: this.currentNamespaceDetails.name,
        namespaceId: this.currentNamespaceDetails.id,
        quantity: this.quantity,
        paymentMethodId: isPurchase ? this.selectedPaymentMethodId : null,
        idempotencyKey: this.idempotencyKeys[this.serializedIdempotencyKeyParams],
        preview: !isPurchase,
        addressDetails: sendCustomerInformation ? this.addressForm : undefined,
        companyDetails: sendCustomerInformation
          ? getCompanyDetails(this.companyDetailsForm, this.addressForm.country)
          : undefined,
      });
    },
    handlePreviewSuccess({ invoice, invoice_item: invoiceItem }) {
      this.subtotal = parseFloat(invoice.amount_without_tax);
      this.tax = parseFloat(invoice.tax_amount);
      this.total = parseFloat(invoice.amount);
      this.subscriptionEndDate = invoiceItem[0].service_end_date;
      this.subscriptionStartDate = invoiceItem[0].service_start_date;
      this.isLoading = false;
    },
    previewTransaction: throttle(function previewTransaction() {
      this.isLoading = true;
      this.resetPreviewData();
      // Abort any previous request
      this.abortController?.abort();
      this.abortController = new AbortController();

      return purchaseAddonSubscription(this.getPayload(false), this.abortController.signal)
        .then(this.handlePreviewSuccess)
        .catch((error) => {
          if (error.code === ERROR_CODE_REQUEST_CANCELED) {
            // This request was aborted, no need to display an error message
            // Keep loading as another one has taken its place
            return;
          }
          this.previewError = error;
          this.isLoading = false;
          this.reportError(error);
        });
    }, DEFAULT_DEBOUNCE_AND_THROTTLE_MS),
    resetPreviewData() {
      this.previewError = null;
      this.subtotal = 0.0;
      this.tax = 0.0;
      this.total = 0.0;
    },
    quantityChange(quantity) {
      this.quantity = quantity;
      this.previewTransaction();
    },
    paymentSuccess(response) {
      if (response.success !== true) {
        throw response.errors;
      }

      // Edit JH
      if (!window.gon?.global?.jh) {
        this.showSuccessModal = true;
      }
    },
    makePayment() {
      this.isLoading = true;
      const payload = this.getPayload(true);

      return purchaseAddonSubscription(payload)
        .then(this.paymentSuccess)
        .catch(this.handlePurchaseError)
        .finally(() => {
          this.isLoading = false;
        });
    },
    handlePurchaseError(error = {}) {
      const { status } = error;
      // Regenerate the idempotency key on client-side error responses (4xx) to ensure the server regards the new request.
      // Context: https://gitlab.com/gitlab-org/gitlab/-/merge_requests/129830#note_1522796835.
      if (status && status >= 400 && status < 500) {
        this.regenerateIdempotencyKey();
      }
      this.apiError = error;
      this.reportError(error);
    },
    reportError(error) {
      Sentry.captureException(error, {
        tags: {
          vue_component: this.$options.name,
        },
      });
    },
    paymentMethodClick(paymentMethodId) {
      this.selectedPaymentMethodId = paymentMethodId;
    },
    updateIdempotencyKey() {
      this.idempotencyKeys[this.serializedIdempotencyKeyParams] =
        this.idempotencyKeys[this.serializedIdempotencyKeyParams] ?? uuidv4();
    },
    regenerateIdempotencyKey() {
      this.idempotencyKeys[this.serializedIdempotencyKeyParams] = uuidv4();
    },
    handleAddressFormChange(data, isValid) {
      this.addressForm = data;
      this.addressFormValid = isValid;
    },
    handleCompanyFormChange(data, isValid) {
      this.companyDetailsForm = data;
      this.companyDetailsFormValid = isValid;
    },
  },
  successPrimaryActionText: { text: 'Manage purchases' },
  PURCHASE_ERROR_DICTIONARY,
  defaultError: PURCHASE_ERROR_DICTIONARY[ERROR_PROCESSING_PAYMENT_TYPE],
};
</script>

<template>
  <div>
    <h1 class="mt-3 gl-font-bold">
      {{ pageTitle }}
    </h1>
    <zuora-availability-wrapper>
      <error-alert
        v-if="error"
        :dismissible="false"
        :error-dictionary="$options.PURCHASE_ERROR_DICTIONARY"
        :default-error="$options.defaultError"
        :error="error"
        :placeholders="{
          namespace: currentNamespaceDetails.name,
        }"
      />
      <gl-alert
        v-else-if="informationAlertText"
        :dismissible="false"
        class="gl-my-5"
        variant="info"
        data-testid="change-namespace-error-alert"
      >
        {{ informationAlertText }}
      </gl-alert>
      <div class="ci-transaction row">
        <div class="col-md-12 col-lg-8">
          <purchase-details-card
            class="col-lg-12 col-md-12 gl-p-0"
            :current-namespace-name="currentNamespaceDetails.name"
            :initial-quantity="quantity"
            :product-title="productTitle"
            :product-name="productName"
            :purchase-details="displayPurchaseDetails"
            :address-form-data="addressForm"
            :company-details-form-data="companyDetailsForm"
            @quantityChange="quantityChange"
            @addressFormChange="handleAddressFormChange"
            @companyFormChange="handleCompanyFormChange"
          />
          <purchase-summary-card
            class="col-lg-12 col-md-12 gl-p-0 gl-pb-4"
            :loading="isLoading"
            :quantity="quantity"
            :price-per-unit="productPrice"
            :charge-description="chargeDescription"
            :price="subtotal"
            :tax="tax"
            :subtotal="subtotal"
            :product-name="productName"
          >
            <template #price-additional-info>
              <div
                v-if="showAdditionalPriceInfo"
                class="gl-mt-2 gl-flex gl-items-center gl-text-gray-500"
                data-testid="subscription-period"
              >
                {{ subscriptionPeriod }}
                <gl-icon
                  v-gl-tooltip.right
                  :title="$options.i18n.tooltipNote"
                  :aria-label="$options.i18n.tooltipNote"
                  class="gl-ml-2"
                  data-testid="subscription-period-tooltip"
                  role="tooltip"
                  name="question"
                />
              </div>
            </template>
          </purchase-summary-card>
        </div>
        <purchase-confirmation-card
          class="col-md-12 col-lg-4"
          :card-title="__('Billing information')"
          :confirm-text="purchaseConfirmationText"
          :disabled="purchaseButtonDisabled"
          :loading="isLoading"
          :total="total"
          @confirmPurchase="makePayment"
        >
          <template #payment-form>
            <payment-methods-select @click="paymentMethodClick" />
          </template>
        </purchase-confirmation-card>
      </div>
      <purchase-success-modal
        :action-primary="$options.successPrimaryActionText"
        :description="successDescription"
        :visible="showSuccessModal"
        :success-redirect-path="successRedirectPath"
      />
    </zuora-availability-wrapper>
  </div>
</template>
